import { watchExperimentFlags } from '../utils/experimentFlagUpdates';
import { useEffect, useRef } from 'react';
import {
  readManualThemeMode,
  setExperimentThemeMode,
  THEME_PREFERENCE_EVENT,
  THEME_STORAGE_KEY,
} from '../design-system/theme';
import {
  getWebsiteThemeAttribution,
  getWebsiteThemeEntryPage,
  isWebsiteThemeTrafficEligible,
  claimWebsiteThemeAssignment,
  WEBSITE_THEME_ASSIGNMENT_KEY,
  websiteThemeFlag,
  WEBSITE_THEME_VERSION,
  WebsiteThemeAttribution,
} from './websiteTheme';

export type WebsiteThemeFlagValue = string | boolean | undefined;
export interface WebsiteThemeFlagClient {
  get_distinct_id(): string;
  getFeatureFlag(key: string, options: { send_event: false }): WebsiteThemeFlagValue;
  onFeatureFlags(callback: () => void): (() => void) | void;
}
interface Props {
  pathname: string;
  client: WebsiteThemeFlagClient | null;
  /** Internal users, previews, and test overrides must be supplied by the owning app. */
  excluded?: boolean;
  /** Existing authenticated users do not enroll; established cohorts remain continuous after signup. */
  enrollmentExcluded?: boolean;
  capture: (event: string, properties: Record<string, string | boolean>) => boolean;
}
const exposed = new Set<string>();
const crossed = new Set<string>();

export function WebsiteThemeExperimentController({
  pathname,
  client,
  excluded = false,
  enrollmentExcluded = false,
  capture,
}: Props): null {
  const readyClient = useRef<WebsiteThemeFlagClient | null>(null);
  useEffect(() => () => setExperimentThemeMode(null), []);
  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    let secondFrame = 0;
    let revision = 0;
    let flagsReady = client !== null && readyClient.current === client;
    let previousManual = readManualThemeMode();
    const eligible = isWebsiteThemeTrafficEligible(window.location.hostname, excluded);
    const evaluate = () => {
      revision += 1;
      const currentRevision = revision;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(secondFrame);
      const previous = getWebsiteThemeAttribution();
      const manual = readManualThemeMode();
      if (manual && previousManual !== manual && previous && eligible) {
        const crossoverKey = `${previous.theme_experiment_key}:${manual}`;
        if (
          !crossed.has(crossoverKey) &&
          capture('theme_experiment_crossover', {
            ...previous,
            theme_mode: manual,
            theme_source: 'manual',
            theme_experiment_crossover: true,
          })
        )
          crossed.add(crossoverKey);
      }
      previousManual = manual;
      if (!eligible || manual || !client || !flagsReady) {
        setExperimentThemeMode(null);
        return;
      }
      if (!previous && enrollmentExcluded) {
        setExperimentThemeMode(null);
        return;
      }
      const family = previous?.theme_experiment_entry_page || getWebsiteThemeEntryPage(pathname);
      if (!family) {
        setExperimentThemeMode(null);
        return;
      }
      const key = websiteThemeFlag(family);
      let variant: WebsiteThemeFlagValue;
      try {
        variant = client.getFeatureFlag(key, { send_event: false });
      } catch {
        setExperimentThemeMode(null);
        return;
      }
      if (variant !== 'control' && variant !== 'test') {
        setExperimentThemeMode(null);
        return;
      }
      // Retain entry-family attribution, but the current flag owns the arm.
      let distinctId: string;
      try {
        distinctId = client.get_distinct_id();
      } catch {
        setExperimentThemeMode(null);
        return;
      }
      if (!distinctId) {
        setExperimentThemeMode(null);
        return;
      }
      const assignment: WebsiteThemeAttribution =
        previous?.theme_experiment_variant === variant
          ? previous
          : {
              posthog_distinct_id: distinctId,
              theme_experiment_key: key,
              theme_experiment_variant: variant,
              theme_experiment_entry_page: family,
              theme_experiment_version: WEBSITE_THEME_VERSION,
            };
      const mode = assignment.theme_experiment_variant === 'test' ? 'light' : 'dark';
      setExperimentThemeMode(mode);
      // Two frames allow React subscribers to commit and the browser to paint before exposure.
      frame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          void (async () => {
            if (
              cancelled ||
              revision !== currentRevision ||
              readManualThemeMode() ||
              document.documentElement.dataset.bsTheme !== mode
            )
              return;
            const winner = await claimWebsiteThemeAssignment(
              assignment,
              () => !cancelled && revision === currentRevision && !readManualThemeMode(),
            );
            if (!winner || cancelled || revision !== currentRevision || readManualThemeMode())
              return;
            if (
              winner.theme_experiment_key !== assignment.theme_experiment_key ||
              winner.theme_experiment_variant !== assignment.theme_experiment_variant ||
              winner.posthog_distinct_id !== assignment.posthog_distinct_id
            ) {
              evaluate();
              return;
            }
            if (document.documentElement.dataset.bsTheme !== mode) return;
            const exposureKey = `${assignment.theme_experiment_key}:${assignment.theme_experiment_variant}`;
            if (exposed.has(exposureKey)) return;
            if (
              capture('experiment_exposure', {
                ...assignment,
                experiment_name: key.replace(/-/g, '_'),
                feature_flag: key,
                feature_flag_value: assignment.theme_experiment_variant,
                variant: assignment.theme_experiment_variant,
                [`$feature/${key}`]: assignment.theme_experiment_variant,
                is_experiment_active: true,
                traffic_environment: 'production',
                theme_mode: mode,
                theme_source: 'experiment',
              })
            )
              exposed.add(exposureKey);
          })();
        });
      });
    };
    const storage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === THEME_STORAGE_KEY ||
        event.key === WEBSITE_THEME_ASSIGNMENT_KEY
      )
        evaluate();
    };
    evaluate();
    const unsubscribe = watchExperimentFlags(client, () => {
      readyClient.current = client;
      flagsReady = true;
      evaluate();
    });
    window.addEventListener(THEME_PREFERENCE_EVENT, evaluate);
    window.addEventListener('storage', storage);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(secondFrame);
      unsubscribe?.();
      window.removeEventListener(THEME_PREFERENCE_EVENT, evaluate);
      window.removeEventListener('storage', storage);
    };
  }, [pathname, client, excluded, enrollmentExcluded, capture]);
  return null;
}
